import React, { useState, useEffect } from 'react';
import { FireIcon, TrendUpIcon, DotsThreeIcon, UserPlusIcon, CaretLeftIcon, CaretRightIcon, ArrowsLeftRightIcon, ChatCircleIcon } from '@phosphor-icons/react';
import { supabase } from '../lib/supabase';
import { useNavigate } from 'react-router-dom';
import { useSendToChat } from '../hooks/useSendToChat';

interface RightPanelProps {
   isOpen: boolean;
   onToggle: () => void;
   side?: 'left' | 'right';
   onSwapSides?: () => void;
   projectId?: string;
}

export const RightPanel: React.FC<RightPanelProps> = ({
   isOpen,
   onToggle,
   side = 'right',
   onSwapSides,
   projectId
}) => {
   const navigate = useNavigate();
   const handleSendToChat = useSendToChat(projectId);
   const [trending, setTrending] = useState<any[]>([]);
   const [creators, setCreators] = useState<any[]>([]);
   const [activeChallenge, setActiveChallenge] = useState<any>(null);

   // State for showing toggle handle on edge hover (when collapsed)
   const [isHandleVisible, setIsHandleVisible] = useState(false);

   useEffect(() => {
      if (!isOpen) return;

      const fetchCommunity = async () => {
         // Fetch Trending
         const { data: trendData } = await supabase
            .from('generations')
            .select('*')
            .eq('is_public', true)
            .order('likes_count', { ascending: false })
            .limit(4);

         if (trendData) setTrending(trendData);

         // Fetch Creators
         const { data: creatorData } = await supabase
            .from('profiles')
            .select('*')
            .order('followers_count', { ascending: false })
            .limit(3);

         if (creatorData) setCreators(creatorData);

         // Fetch Active Challenge
         const { data: challengeData } = await supabase
            .from('weekly_challenges')
            .select('*')
            .eq('is_active', true)
            .maybeSingle();

         if (challengeData) setActiveChallenge(challengeData);
      };

      fetchCommunity();
   }, [isOpen]);

   return (
      <>
         {/* Edge hover zone - reveals toggle handle when collapsed */}
         {!isOpen && (
            <div
               className={`fixed top-0 h-screen w-6 z-40 hidden xl:block ${side === 'left' ? 'left-0' : 'right-0'}`}
               onMouseEnter={() => setIsHandleVisible(true)}
               onMouseLeave={() => setIsHandleVisible(false)}
            />
         )}

         <div
            className={`
        hidden xl:flex flex-col fixed top-0 h-screen bg-onyx/95 backdrop-blur-xl z-40
        transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)]
        ${side === 'right' ? 'right-0' : 'left-0'}
        ${isOpen ? 'w-[640px]' : 'w-0'}
        ${isOpen && (side === 'right' ? 'border-l border-white/5' : 'border-r border-white/5')}
      `}
         >



            {/* Content Container */}
            <div className={`flex flex-col h-full w-[640px] transition-opacity duration-200 ${isOpen ? 'opacity-100 delay-100' : 'opacity-0 pointer-events-none'} relative z-10`}>
               {/* Swap Side Button (Absolute Positioned) - Only show when on Right side */}
               {onSwapSides && side === 'right' && (
                  <button
                     onClick={onSwapSides}
                     className="absolute top-6 right-4 z-50 p-2 text-zinc-600 hover:text-white hover:bg-white/5 rounded-full transition-colors"
                     title="Swap Sidebar Position"
                  >
                     <ArrowsLeftRightIcon className="w-4 h-4" />
                  </button>
               )}
               {/* Header */}
               <div className="h-20 flex items-end justify-between px-6 pb-4 flex-shrink-0">
                  <h3 className="text-xl font-heading font-bold text-white">Community</h3>

               </div>

               <div className="flex-1 overflow-y-auto p-6 space-y-8 scrollbar-hide">

                  {/* Trending Now */}
                  <div>
                     <div className="flex items-center gap-2 mb-4">
                        <FireIcon className="w-4 h-4 text-orange-400" />
                        <span className="text-xs font-medium tracking-wider uppercase text-zinc-500">Trending Now</span>
                     </div>

                     <div className="grid grid-cols-2 gap-3">
                        {trending.map(item => (
                           <TrendingItem
                              key={item.id}
                              img={item.image_url || item.img}
                              onSendToChat={projectId ? handleSendToChat : undefined}
                           />
                        ))}
                        {trending.length === 0 && (
                           <p className="text-zinc-500 text-sm italic col-span-2">No trending posts yet.</p>
                        )}
                     </div>
                  </div>

                  {/* Top Creators */}
                  <div>
                     <div className="flex items-center gap-2 mb-4">
                        <TrendUpIcon className="w-4 h-4 text-emerald-400" />
                        <span className="text-xs font-medium tracking-wider uppercase text-zinc-500">Top Creators</span>
                     </div>

                     <div className="space-y-4">
                        {creators.map(c => (
                           <CreatorItem
                              key={c.id}
                              name={c.full_name || c.username || 'Anon'}
                              handle={c.username ? `@${c.username}` : '@anon'}
                              avatar={c.avatar_url}
                           />
                        ))}
                     </div>
                  </div>

                  {/* Challenge Card */}
                  {activeChallenge && (
                     <div className="p-5 rounded-xl border border-white/10 bg-gradient-to-br from-purple-900/20 to-blue-900/20 relative overflow-hidden group">
                        <div className="absolute inset-0 bg-white/5 opacity-0 group-hover:opacity-100 transition-opacity" />
                        <span className="inline-block px-2 py-1 rounded bg-white/10 text-[10px] font-medium text-white mb-2 border border-white/10">WEEKLY CHALLENGE</span>
                        <h4 className="text-lg font-heading font-bold text-white mb-1">{activeChallenge.title}</h4>
                        <p className="text-xs text-zinc-400 mb-3">{activeChallenge.description?.slice(0, 100)}...</p>
                        <button
                           onClick={() => navigate('/scenes')}
                           className="w-full py-2 bg-white text-black text-xs font-bold rounded shadow-lg hover:bg-zinc-200 transition-colors"
                        >
                           Join Challenge
                        </button>
                     </div>
                  )}

               </div>
            </div>
         </div>

         {/* Sleek Toggle Handle - Flush with sidebar, hidden when collapsed until hover */}
         <button
            onClick={onToggle}
            onMouseEnter={() => !isOpen && setIsHandleVisible(true)}
            onMouseLeave={() => !isOpen && setIsHandleVisible(false)}
            className={`
            fixed top-0 bottom-0 cursor-pointer flex items-center justify-center group z-30 outline-none
            transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)]
            ${isOpen
               ? 'w-[2px] bg-white/5 hover:w-1 hover:bg-white/10'
               : isHandleVisible
                  ? 'w-6 bg-onyx/95 hover:bg-zinc-900 backdrop-blur-xl'
                  : 'w-0 opacity-0 pointer-events-none'}
            ${side === 'right'
                  ? `${isOpen ? 'right-[640px]' : 'right-0'} ${!isOpen && isHandleVisible && 'border-l border-white/5'}`
                  : `${isOpen ? 'left-[640px]' : 'left-0'} ${!isOpen && isHandleVisible && 'border-r border-white/5'}`}
            hidden xl:flex
         `}
            title={isOpen ? "Collapse Panel" : "Expand Panel"}
         >
            {/* Chevron Indicator - Only visible when collapsed and handle is shown */}
            {!isOpen && isHandleVisible && (
               <div className="opacity-40 group-hover:opacity-100 transition-opacity">
                  {side === 'right' ? (
                     <CaretLeftIcon className="w-3 h-3 text-white" />
                  ) : (
                     <CaretRightIcon className="w-3 h-3 text-white" />
                  )}
               </div>
            )}
         </button>

      </>
   );
};

const TrendingItem = ({ img, onSendToChat }: { img: string; onSendToChat?: (img: string) => void }) => (
   <div className="aspect-square rounded-lg overflow-hidden border border-white/5 cursor-pointer group relative">
      <img src={img} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" alt="Art" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end justify-end p-3">
         {onSendToChat && (
            <button
               onClick={(e) => { e.stopPropagation(); onSendToChat(img); }}
               className="p-2 rounded-lg bg-white/10 hover:bg-white/20 backdrop-blur-md text-white transition-colors"
               title="Send to chat"
            >
               <ChatCircleIcon className="w-4 h-4" />
            </button>
         )}
      </div>
   </div>
);

const CreatorItem = ({ name, handle, avatar }: { name: string, handle: string, avatar?: string }) => (
   <div className="flex items-center justify-between group cursor-pointer">
      <div className="flex items-center gap-3">
         <div className="w-8 h-8 rounded-full bg-zinc-800 border border-white/10 flex items-center justify-center text-xs font-bold text-white overflow-hidden">
            {avatar ? (
               <img src={avatar} className="w-full h-full object-cover" alt={name} />
            ) : (
               name[0]?.toUpperCase()
            )}
         </div>
         <div>
            <div className="text-sm font-medium text-zinc-200 group-hover:text-white transition-colors">{name}</div>
            <div className="text-[10px] text-zinc-500">{handle}</div>
         </div>
      </div>
      <button className="text-zinc-500 hover:text-white transition-colors">
         <UserPlusIcon className="w-4 h-4" />
      </button>
   </div>
);